import { throttle } from 'es-toolkit';
import { useLayoutEffect } from 'react';

export default function useSyncDataTableHeaders({
  pinTableHeader,
  stickyTableHeaderRef,
  fixedTableHeaderRef,
}) {
  useLayoutEffect(() => {
    if (!pinTableHeader) return;

    let stickyTableHeader: HTMLDivElement = stickyTableHeaderRef.current;
    let fixedTableHeader: HTMLDivElement = fixedTableHeaderRef.current;
    let fixedHeaders: NodeListOf<HTMLTableCellElement>;
    let stickyHeaders: NodeListOf<HTMLTableCellElement>;

    function syncTableScroll() {
      if (!stickyTableHeader || !fixedTableHeader) return;
      stickyTableHeader.scrollLeft = fixedTableHeader.scrollLeft;
    }

    function syncColumnWidths() {
      if (!stickyTableHeader || !fixedTableHeader) return;

      fixedHeaders.forEach((header, index) => {
        stickyHeaders[index].style.minWidth = `${header.getBoundingClientRect().width}px`;
      });
    }

    function setRowOffset() {
      if (!stickyTableHeader || !fixedTableHeader) return;

      fixedTableHeader.style.marginTop = `-${stickyTableHeader.clientHeight}px`;
    }

    const handleResize = () => {
      syncColumnWidths();
      setRowOffset();
    };

    const throttledHandleResize = throttle(handleResize, 20);

    const resizeObserver = new ResizeObserver(() => {
      throttledHandleResize();
    });

    const attachResizeObserver = () => {
      if (!stickyTableHeader || !fixedTableHeader) return;

      fixedHeaders.forEach((header) => {
        resizeObserver.observe(header);
      });
    };

    function initScrollSync() {
      stickyTableHeader = stickyTableHeaderRef.current;
      fixedTableHeader = fixedTableHeaderRef.current;
      fixedHeaders = fixedTableHeader.querySelectorAll('th');
      stickyHeaders = stickyTableHeader.querySelectorAll('th');

      attachResizeObserver();
      syncColumnWidths();
      setRowOffset();

      fixedTableHeader?.addEventListener('scroll', syncTableScroll, true);
      window.addEventListener('resize', throttledHandleResize, true);
    }

    let currentCheckInterval: number | null = null;

    if (!stickyTableHeaderRef.current || !fixedTableHeaderRef.current) {
      currentCheckInterval = window.setInterval(() => {
        if (stickyTableHeaderRef.current && fixedTableHeaderRef.current) {
          if (currentCheckInterval !== null) {
            window.clearInterval(currentCheckInterval);
            currentCheckInterval = null;
          }
          initScrollSync();
        }
      }, 100);
    }

    return () => {
      if (currentCheckInterval !== null) {
        window.clearInterval(currentCheckInterval);
      }
      fixedTableHeader?.removeEventListener('scroll', syncTableScroll, true);
      window.removeEventListener('resize', throttledHandleResize, true);
      resizeObserver.disconnect();
    };
  }, [pinTableHeader, stickyTableHeaderRef, fixedTableHeaderRef]);
}
